import React from 'react';
import styled from 'styled-components';
import {
  cta2FontWeight,
  spacer16,
  spacer40,
  spacer80,
  screenSmMax,
} from '@spotify-internal/encore-foundation';
import { ModuleType } from '../index';
import { ContentfulAsset } from '../../Contentful';
import {
  DELAYS,
  FeaturedContentHeadline,
  HeadlineWithSupertextComponent,
  withAnimationContext,
} from '../../ui';
import { Testimonial } from './Testimonial';
import { fadeInAnimation } from '../../ui/Animations';

const SingleTestimonial = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.box.text};
  background-color: ${({ theme }) => theme.box.background};
  @media (max-width: ${screenSmMax}) {
    flex-wrap: nowrap;
    flex-direction: column;
  }
`;

const StyledTestimonial = styled.div`
  width: 65%;
  padding-left: ${spacer80};
  @media (max-width: ${screenSmMax}) {
    padding-left: 0;
    width: 100%;
    order: 4;
    flex: 1;
  }
`;

const StyledFCM = styled.div`
  flex: 0 0 100%;
  padding-bottom: ${spacer40};
  @media (max-width: ${screenSmMax}) {
    padding-bottom: ${spacer16};
    order: 2;
  }
`;

const BodyDesktop = withAnimationContext(
  styled.div`
    ${fadeInAnimation(DELAYS.THIRD)};
    @media (max-width: ${screenSmMax}) {
      display: none;
    }
  `,
);

const BodyMobile = styled.div`
  display: none;
  @media (max-width: ${screenSmMax}) {
    display: inline;
    padding-bottom: ${spacer16};
    font-weight: ${cta2FontWeight};
    width: 100%;
    order: 3;
    flex: 1;
    line-height: initial;
  }
`;

const StyledHWSC = styled.div`
  width: 35%;
  @media (max-width: ${screenSmMax}) {
    width: 100%;
    order: 1;
    flex: 1;
  }
`;

type SingleTestimonialModuleType = ModuleType & {
  supertext: string;
  headline: string;
  body: string;
  quote: string;
  image: ContentfulAsset;
  source: string;
  jobTitle: string;
  caption?: string;
};

export const SingleTestimonialModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  SingleTestimonialModuleType
>(
  (
    {
      supertext = '',
      headline = '',
      body = '',
      quote = '',
      image,
      source = '',
      jobTitle = '',
      caption = '',
    },
    ref,
  ) => {
    const fchProps = {
      supertext,
      headline,
      image,
      caption,
      useFeaturedContentHeadline: false,
    };
    const quoteProps = {
      quote,
      source,
      jobTitle,
    };

    return (
      <SingleTestimonial ref={ref as React.RefObject<HTMLDivElement>}>
        <StyledFCM>
          <FeaturedContentHeadline {...fchProps} />
        </StyledFCM>
        <StyledHWSC>
          <HeadlineWithSupertextComponent
            supertext={supertext}
            headline={headline}
          />
          <BodyDesktop>{body}</BodyDesktop>
        </StyledHWSC>
        <BodyMobile>{body}</BodyMobile>
        <StyledTestimonial>
          <Testimonial {...quoteProps} />
        </StyledTestimonial>
      </SingleTestimonial>
    );
  },
);
